import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Globe, Sparkles, Loader2, Trash2, AlertTriangle } from "lucide-react";
import {
  getWorkspaceTemplateStatus,
  publishTemplate,
  unpublishTemplate,
} from "@/lib/templates.functions";
import { deleteWorkspace, listMyWorkspaces } from "@/lib/workspaces.functions";
import { WorkspaceIntegrationOverrides } from "@/components/workspace-integration-overrides";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const { workspaceId } = Route.useParams();
  const navigate = useNavigate();
  const getStatus = useServerFn(getWorkspaceTemplateStatus);
  const publishFn = useServerFn(publishTemplate);
  const unpublishFn = useServerFn(unpublishTemplate);
  const deleteFn = useServerFn(deleteWorkspace);
  const listWs = useServerFn(listMyWorkspaces);
  const qc = useQueryClient();
  const [confirmName, setConfirmName] = useState("");

  const { data: wsList } = useQuery({
    queryKey: ["my-workspaces"],
    queryFn: () => listWs(),
  });
  const currentWs = wsList?.workspaces.find((w: any) => w.id === workspaceId);

  const { data, isLoading } = useQuery({
    queryKey: ["workspace-template-status", workspaceId],
    queryFn: () => getStatus({ data: { workspace_id: workspaceId } }),
  });

  const publish = useMutation({
    mutationFn: () => publishFn({ data: { template_id: data!.template!.id } }),
    onSuccess: () => {
      toast.success("Published as official template.");
      qc.invalidateQueries({ queryKey: ["workspace-template-status", workspaceId] });
      qc.invalidateQueries({ queryKey: ["program-templates"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Publish failed"),
  });
  const unpublish = useMutation({
    mutationFn: () => unpublishFn({ data: { template_id: data!.template!.id } }),
    onSuccess: () => {
      toast.success("Reverted to draft candidate.");
      qc.invalidateQueries({ queryKey: ["workspace-template-status", workspaceId] });
      qc.invalidateQueries({ queryKey: ["program-templates"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Unpublish failed"),
  });

  const del = useMutation({
    mutationFn: () =>
      deleteFn({ data: { workspace_id: workspaceId, confirm_name: confirmName } }),
    onSuccess: () => {
      toast.success("Workspace deleted.");
      qc.invalidateQueries({ queryKey: ["my-workspaces"] });
      navigate({ to: "/" });
    },
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-primary mt-0.5" />
          <div className="flex-1">
            <h2 className="text-base font-medium">Template publishing</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Your workspace configuration is auto-saved as a reusable template. Platform
              admins can publish it as an official template so other users can adopt it in
              one click when creating a new workspace.
            </p>

            {isLoading ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : !data?.template ? (
              <p className="mt-4 text-sm text-muted-foreground">
                No candidate snapshot yet. Save any product or classification change to
                create one.
              </p>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{data.template.name}</span>
                  {data.template.status === "official" ? (
                    <Badge className="gap-1"><Globe className="h-3 w-3" /> Official</Badge>
                  ) : (
                    <Badge variant="secondary">Draft candidate</Badge>
                  )}
                  {data.template.usage_count > 0 && (
                    <span className="text-xs text-muted-foreground">
                      · used by {data.template.usage_count}
                    </span>
                  )}
                </div>

                {data.is_platform_admin ? (
                  <div className="flex gap-2">
                    {data.template.status === "official" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unpublish.mutate()}
                        disabled={unpublish.isPending}
                      >
                        {unpublish.isPending && <Loader2 className="h-3 w-3 animate-spin mr-2" />}
                        Unpublish
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => publish.mutate()}
                        disabled={publish.isPending}
                      >
                        {publish.isPending && <Loader2 className="h-3 w-3 animate-spin mr-2" />}
                        Publish as official template
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Only platform admins can publish official templates.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <WorkspaceIntegrationOverrides workspaceId={workspaceId} />

      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
          <div className="flex-1">
            <h2 className="text-base font-medium text-destructive">Danger zone</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Permanently delete this workspace and all of its data, contacts, transactions,
              imports, segments, schedules, anomalies, and activity. This cannot be undone.
              Only account owners or admins can delete a workspace.
            </p>

            <AlertDialog
              onOpenChange={(open) => {
                if (!open) setConfirmName("");
              }}
            >
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" className="mt-4 gap-2">
                  <Trash2 className="h-4 w-4" />
                  Delete workspace
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this workspace?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete{" "}
                    <span className="font-medium text-foreground">
                      {currentWs?.program_name ?? "this workspace"}
                    </span>{" "}
                    and every record inside it. There is no recovery.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-2">
                  <Label htmlFor="confirm-name" className="text-sm">
                    Type{" "}
                    <span className="font-mono font-medium text-foreground">
                      {currentWs?.program_name ?? "the workspace name"}
                    </span>{" "}
                    to confirm
                  </Label>
                  <Input
                    id="confirm-name"
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={currentWs?.program_name ?? ""}
                    autoComplete="off"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={
                      del.isPending ||
                      !currentWs ||
                      confirmName.trim() !== currentWs.program_name.trim()
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      del.mutate();
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {del.isPending && <Loader2 className="h-3 w-3 animate-spin mr-2" />}
                    Delete permanently
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
